import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import BloqLogo from "@/components/BloqLogo";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

function startGoogleOAuth() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      const from = location.state?.from || "/app";
      navigate(from);
      toast.success("Welcome back to Bloq");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not sign you in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bloq-container pt-7">
        <BloqLogo size={26} />
      </div>
      <div className="flex-1 grid lg:grid-cols-2 items-center bloq-container py-12 gap-16">
        <div className="max-w-md mx-auto w-full" data-testid="login-card">
          <p className="bloq-eyebrow mb-3">Welcome back</p>
          <h1 className="text-4xl sm:text-5xl bloq-headline mb-3">
            Pick up where you left off.
          </h1>
          <p className="text-[#4a4a52] text-base leading-relaxed mb-10">
            Sign in and your meetings, follow-ups, and bookings are exactly where you parked them.
          </p>

          <button
            data-testid="login-google-button"
            onClick={startGoogleOAuth}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all mb-4 font-medium"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48">
              <path
                fill="#FFC107"
                d="M43.6 20.5H42V20H24v8h11.3C33.6 32.6 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
              />
              <path
                fill="#FF3D00"
                d="m6.3 14.7 6.6 4.8C14.7 16.2 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
              />
              <path
                fill="#4CAF50"
                d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.4-11.2-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
              />
              <path
                fill="#1976D2"
                d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2c-.4.4 6.5-4.7 6.5-14.8 0-1.2-.1-2.3-.4-3.5z"
              />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-black/8" />
            <p className="text-xs uppercase tracking-widest text-[#82828c]">or with email</p>
            <div className="flex-1 h-px bg-black/8" />
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bloq-input mt-1"
                data-testid="login-email-input"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                  Password
                </label>
                <Link
                  to="/auth/forgot"
                  className="text-xs text-[#005e7a] hover:underline"
                  data-testid="login-forgot-link"
                >
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bloq-input mt-1"
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit-button"
              className="bloq-btn-primary mt-3 flex items-center justify-center gap-2"
              disabled={busy}
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <p className="mt-7 text-sm text-[#4a4a52]">
            Don't have an account?{" "}
            <Link to="/beta-disclaimer" className="font-medium text-[#005e7a]" data-testid="login-to-signup-link">
              Start free
            </Link>
          </p>
        </div>

        <div className="hidden lg:block">
          <div className="bloq-glass-strong p-8 relative overflow-hidden">
            <div className="bloq-hero-orb" style={{ width: 400, height: 400, top: -100, right: -120 }} />
            <p className="bloq-eyebrow">Why teams choose Bloq</p>
            <h3 className="bloq-headline text-2xl mt-2 mb-6 max-w-sm">
              Less tool switching. More actual progress.
            </h3>
            <ul className="flex flex-col gap-4 text-[#4a4a52]">
              {[
                "Run secure HD video rooms in seconds",
                "AI summaries draft your follow-up email for you",
                "Share one link to let clients book themselves in",
                "Everything in one workspace — no more tab roulette",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span
                    className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                    style={{ background: "#005e7a" }}
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
